/**
 * Convert a string value to a boolean
 * If the value is "true", it returns true, otherwise false
 * If the value is already a boolean, it is returned as is
 *
 * @param {*} value - value to be converted to boolean
 * @returns boolean value
 */
export function toBoolean(value) {
  return value === "true" || value === true;
}

/**
 * Convert a string value to a CSS unit
 * If the value can not be parsed, the value is returned as is
 *
 * @param {String} value - value to be converted to CSS unit
 * @param {String} unit - unit to be used
 * @returns {String}
 *
 * @example
 * toCssUnit("12") // "12rem"
 * toCssUnit("12.5") // "12.5rem"
 * toCssUnit("12.5rem") // "12.5rem"
 * toCssUnit("12px") // "12px"
 * toCssUnit("12", "px") // "12px"
 * toCssUnit("12vh") // "12vh"
 */
export function toCssUnit(value, unit = "rem") {
  const number = Number(value);

  return isNaN(number) ? value : `${number}${unit}`;
}

/**
 * Convert a string value to a number
 * If the value can not be parsed, undefined is returned
 * If the value can not be parsed, but returnValue is true, the value is returned
 *
 * @param {String} value - value to be converted to number
 * @param {Boolean} returnValue - return the value if it can not be parsed
 * @returns {Object|undefined}
 */
export function toNumber(value, defaultValue = undefined) {
  const parsedValue = Number(value);

  if (isNaN(parsedValue)) {
    return defaultValue;
  }

  return parsedValue;
}

/**
 * Convert a string value to a number
 * If the value can not be parsed, undefined is returned
 * If the value can not be parsed, but returnValue is true, the value is returned
 *
 * @param {String} value - value to be converted
 * @param {Boolean} returnValue - return the value if it can not be parsed
 * @returns {Object|undefined}
 */
export function toJSON(value, returnValue = false) {
  try {
    if (typeof value === "string") {
      return JSON.parse(value);
    }
  } catch (err) {
    console.log(err);
  }
  if (returnValue) {
    return value;
  }
}

/**
 * Trim a string value and return undefined if the value is empty
 * If the value is not a string, it is returned as is
 *
 * @param {*} value - value to be trimmed
 * @returns value trimmed or undefined if value is empty
 */
export function trimToUndefined(value) {
  if (typeof value === "string") {
    return value.trim() || undefined;
  }
  return value;
}

/**
 * Trim a string value and return null if the value is empty
 * If the value is not a string, it is returned as is
 *
 * @param {*} value - value to be trimmed
 * @returns value trimmed or null if value is empty
 */
export function trimToNull(value) {
  return trimToUndefined(value) ?? null;
}

export function toArray(value) {
  return Array.isArray(value) ? value : [value];
}

/**
 * Convert a number to a CSS spacing reference property.
 *
 * @param {Number} value - value to be converted to CSS spacing
 * @returns string - CSS spacing property
 *
 * @example
 * toCssSpacingProperty(4) // var(--sp-ref-spacing-4)
 * toCssSpacingProperty(8) // var(--sp-ref-spacing-8)
 * toCssSpacingProperty(12) // var(--sp-ref-spacing-12)
 */
export function toCssSpacingProperty(value) {
  return toCssRefProperty("spacing", value);
}

/**
 * Construct a CSS property reference.
 * @param {string} category - category of the CSS property
 * @param {*} value - value of the CSS property
 * @returns string - CSS property
 *
 * @example
 * toCssRefProperty("spacing", 4) // var(--sp-ref-spacing-4)
 * toCssRefProperty("color", "primary") // var(--sp-ref-color-primary)
 */
export function toCssRefProperty(category, value) {
  return `var(--sp-ref-${category}-${value})`;
}
